import React, { useCallback, useEffect, useState } from 'react';

import { Table } from 'components';
import { SecurityUser } from 'iconsax-react';

import { organizationColumns } from './OrganizationsColumns';

import { useNavigate, useSelector, useTranslation } from 'third-party';

import { OrganizationType } from 'constants/common';
import { superAdminOrganizationUrl } from 'constants/url';

import { useSuperAdmin } from 'modules/superAdmin';

import { superAdminSelectors } from 'selectors';

import { FilterValue } from 'components/Table/types';

import {
  APIOrganization,
  APIStringOperators,
  APIStringSearchFilter,
} from 'types/api';

import { palette } from 'styles/theme';

export const SuperAdminPage = () => {
  const { t } = useTranslation();
  const { loadAllOrganizations } = useSuperAdmin();
  const navigate = useNavigate();

  const organizations = useSelector(superAdminSelectors.allOrganizations);
  const allLoaded = useSelector(superAdminSelectors.allOrganizationsLoaded);
  const pending = useSelector(superAdminSelectors.loadAllOrganizationsPending);

  const [columns, setColumns] = useState(organizationColumns());
  const [filters, setFilters] = useState<Record<string, any>>({});

  useEffect(() => {
    // The three Type options are mutually exclusive, so Live and Sandbox have to
    // exclude disabled organizations — those are listed under Disabled instead.
    // 'All'/unset omits both flags.
    const flagsByType = {
      [OrganizationType.Live]: { isSandbox: false, isDisabled: false },
      [OrganizationType.Sandbox]: { isSandbox: true, isDisabled: false },
      [OrganizationType.Disabled]: { isSandbox: undefined, isDisabled: true },
    };
    const typeFlags = flagsByType[filters.type as OrganizationType];

    // Name/description use the advanced string filter, which emits a
    // { condition, value } object. The operator has to reach the API, otherwise
    // every condition behaves like the server-side default. A bare string can
    // still arrive from the simple filter, and means "contains".
    const stringFilter = (filter: any): APIStringSearchFilter | undefined => {
      if (typeof filter === 'string') {
        return filter
          ? { condition: APIStringOperators.Contains, value: filter }
          : undefined;
      }
      return filter?.value ? filter : undefined;
    };

    loadAllOrganizations({
      name: stringFilter(filters.name),
      description: stringFilter(filters.description),
      isSandbox: typeFlags?.isSandbox,
      isDisabled: typeFlags?.isDisabled,
    });
  }, [filters, loadAllOrganizations]);

  const onColumnFilterChange = useCallback(
    (columnId: string, value: string) => {
      setColumns(prev =>
        prev.map(col =>
          col.dataId === columnId ? { ...col, filter: value } : col,
        ),
      );
      setFilters(prev => ({ ...prev, [columnId]: value }));
    },
    [],
  );

  const onAdvancedFilterChange = useCallback(
    (columnId: string, value: FilterValue) => {
      setColumns(prev =>
        prev.map(col =>
          col.dataId === columnId ? { ...col, filterValue: value } : col,
        ),
      );
      setFilters(prev => ({ ...prev, [columnId]: value }));
    },
    [],
  );

  const onFilterReset = useCallback(() => {
    setFilters({});
    setColumns(prev =>
      prev.map(col => ({ ...col, filter: undefined, filterValue: undefined })),
    );
  }, []);

  const onColumnVisibilityChanged = useCallback(
    (columnId: string, value: boolean) => {
      setColumns(prev =>
        prev.map(col =>
          col.dataId === columnId ? { ...col, hidden: !value } : col,
        ),
      );
    },
    [],
  );

  const handleRowClick = useCallback(
    (org: APIOrganization) => {
      navigate(superAdminOrganizationUrl(org.id));
    },
    [navigate],
  );

  return (
    <Table<APIOrganization>
      header={t('superAdmin.header')}
      subHeader={t('superAdmin.subHeader')}
      headerIcon={
        <SecurityUser variant="TwoTone" color={palette.primary900} size="28" />
      }
      filterable
      columns={columns}
      items={organizations}
      allItemsLoaded
      itemsLoaded={allLoaded}
      loadItemsPending={pending}
      onRowClick={handleRowClick}
      onColumnFilterChange={onColumnFilterChange}
      onAdvancedFilterChange={onAdvancedFilterChange}
      onFilterReset={onFilterReset}
      onColumnVisibilityChanged={onColumnVisibilityChanged}
      onColumnSizeChanged={(id, width) => {
        setColumns(prev =>
          prev.map(col => (col.dataId === id ? { ...col, width } : col)),
        );
      }}
      bodyRowHeight="1rem"
      headerRowHeight="0.2rem"
      tableMinHeight="20rem"
      emptyText={t('superAdmin.empty')}
    />
  );
};
